import React from 'react';
import { useTranslation } from 'react-i18next';
import { s } from '../helpers';

export interface TopColumnItem {
  key: string;
  label: string;
  color: string;
  weight: number;
  icon: string;
  onClick: () => void;
}

export interface ManagerRowItem {
  accountId: string;
  cmName: string;
  alias: string;
  initial: string;
  platform: string;
  platformTag: string;
  stage: string;
  stageColor: string;
  stageBg: string;
  capital: string;
  balance: string;
  pnl: string;
  pnlColor: string;
  dd: string;
  ddColor: string;
  days: number;
  status: string;
  statusColor: string;
  onClick: () => void;
  rank: number;
  medalBg: string;
  medalBorder: string;
  medalColor: string;
  cmId: string;
  level: string;
  payoutMark: string;
  payoutColor: string;
  payoutSize: string;
  discipline: number;
  risk: number;
  consistency: number;
  score: number;
  trendMark: string;
  trendColor: string;
  trendSize: string;
  trendTitle: string;
}

export interface TopSortOption {
  v: string;
  l: string;
}

export interface TopManagersSectionProps {
  tableTotal: number;
  topCards: boolean;
  topTable: boolean;
  topSortKey: string;
  onTopSort: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  topSortOpts: TopSortOption[];
  topCols: TopColumnItem[];
  rows: ManagerRowItem[];
  noRows: boolean;
  hasMore: boolean;
  moreH: string;
  moreLabel: string;
  toggleMore: () => void;
  selectedTierCapital?: number | null;
  onClearTierFilter?: () => void;
}

export const TopManagersSection: React.FC<TopManagersSectionProps> = ({
  tableTotal,
  topCards,
  topTable,
  topSortKey,
  onTopSort,
  topSortOpts,
  topCols,
  rows,
  noRows,
  hasMore,
  moreH,
  moreLabel,
  toggleMore,
  selectedTierCapital,
  onClearTierFilter
}) => {
  const { t } = useTranslation();

  return (
    <>
      <section
        style={s(
          `background:#FFFEFB;border:1px solid #EFE6D6;border-radius:12px;box-shadow:0 1px 3px rgba(120,90,30,0.06);min-width:0;padding:16px 18px;display:flex;flex-direction:column;gap:12px`
        )}
      >
        <div style={s(`display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:8px`)}>
          <div style={s(`display:flex;align-items:center;gap:10px;flex-wrap:wrap`)}>
            <h2
              style={s(
                `margin:0;font-size:14px;font-weight:700;color:#1B1B1B;text-transform:uppercase;letter-spacing:0.01em`
              )}
            >
              {t('managerNetwork.top.title', 'Top Capital Managers')}
            </h2>
            {selectedTierCapital ? (
              <span
                style={s(
                  `display:inline-flex;align-items:center;gap:6px;padding:2px 8px;border-radius:12px;background:#FBF3E3;border:1px solid #E6DCCB;font-size:11.5px;font-weight:600;color:#A67A2E`
                )}
              >
                <span>{`Tier: $${selectedTierCapital >= 1000 ? selectedTierCapital / 1000 + 'K' : selectedTierCapital}`}</span>
                {onClearTierFilter && (
                  <button
                    onClick={onClearTierFilter}
                    title={t('managerNetwork.top.clearTierFilter', 'Xóa lọc tier')}
                    style={s(`border:0;background:transparent;cursor:pointer;color:#A67A2E;font-size:11px;padding:0;line-height:1;display:flex;align-items:center`)}
                  >
                    ✕
                  </button>
                )}
              </span>
            ) : null}
          </div>
          <span style={s(`font-size:11.5px;color:#6B6358`)}>
            {t('managerNetwork.top.subtitle', '{{count}} CMs · click a row for detail', { count: tableTotal })}
          </span>
        </div>

        {Boolean(topCards) && (
          <>
            <label
              style={s(
                `position:relative;display:flex;align-items:center;gap:8px;height:40px;border:1px solid #E6DCCB;border-radius:8px;padding:0 10px;background:#fff`
              )}
            >
              <i className="icon-arrow-up-down" style={s(`color:#6B6358`)}></i>
              <select
                value={topSortKey}
                onChange={onTopSort}
                style={s(
                  `flex:1;min-width:0;border:0;outline:0;background:transparent;font-size:13px;color:#1B1B1B;padding-right:22px`
                )}
              >
                {topSortOpts?.map((o, o_idx) => (
                  <React.Fragment key={o_idx}>
                    <option value={o.v}>{o.l}</option>
                  </React.Fragment>
                ))}
              </select>
              <i className="icon-chevron-down" style={s(`position:absolute;right:10px;color:#6B6358;pointer-events:none`)}></i>
            </label>
            <div style={s(`display:flex;flex-direction:column`)}>
              {rows?.map((r, r_idx) => (
                <React.Fragment key={r_idx}>
                  <div
                    onClick={r.onClick}
                    style={s(
                      `display:grid;grid-template-columns:28px minmax(0,1fr) auto;gap:10px;align-items:center;padding:12px 2px;border-bottom:1px solid #F4EEE3;cursor:pointer;min-height:56px`
                    )}
                  >
                    <span
                      style={s(
                        `width:24px;height:24px;border-radius:50%;background:${r.medalBg};border:1.5px solid ${r.medalBorder};color:${r.medalColor};font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center`
                      )}
                    >
                      {r.rank}
                    </span>
                    <div style={s(`display:flex;flex-direction:column;gap:3px;min-width:0`)}>
                      <div style={s(`display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;font-size:13px`)}>
                        <span style={s(`font-weight:600;color:#1B1B1B`)}>{r.cmId}</span>
                        <span style={s(`color:${r.stageColor};font-weight:500;font-size:12px`)}>{r.stage}</span>
                        <span style={s(`color:#3A342C;font-size:12px`)}>{r.capital}</span>
                        <span style={s(`color:${r.payoutColor};font-weight:700;font-size:12px`)}>{r.payoutMark}</span>
                      </div>
                      <span style={s(`font-size:11.5px;color:#6B6358`)}>
                        {t('managerNetwork.top.mobileStats', 'Discipline {{discipline}} · Risk {{risk}} · Consistency {{consistency}}', {
                          discipline: r.discipline,
                          risk: r.risk,
                          consistency: r.consistency
                        })}
                      </span>
                    </div>
                    <div style={s(`display:flex;flex-direction:column;align-items:flex-end;gap:2px`)}>
                      <span style={s(`font-size:17px;font-weight:700;color:#1B1B1B`)}>{r.score}</span>
                      <span style={s(`color:${r.trendColor};font-size:${r.trendSize};font-weight:700`)}>{r.trendMark}</span>
                    </div>
                  </div>
                </React.Fragment>
              ))}
              {Boolean(noRows) && (
                <div style={s(`padding:24px;text-align:center;color:#6B6358;font-size:13px`)}>
                  {t('managerNetwork.top.noMatch', 'No Capital Managers match the current search and filters.')}
                </div>
              )}
            </div>
          </>
        )}

        {Boolean(topTable) && (
          <div style={s(`overflow-x:auto`)}>
            <div style={s(`min-width:820px`)}>
              <div
                style={s(
                  `display:grid;grid-template-columns:44px minmax(90px,1.1fr) 64px minmax(96px,1.1fr) 64px repeat(3,minmax(80px,1fr)) minmax(96px,1.1fr) 60px;gap:8px;padding:8px 6px;border-bottom:1px solid #EFE6D6;font-size:11.5px;color:#3A342C;text-align:center`
                )}
              >
                <span>#</span>
                {topCols?.map((c, c_idx) => (
                  <React.Fragment key={c_idx}>
                    <button
                      onClick={c.onClick}
                      style={s(
                        `border:0;background:transparent;padding:0;font-size:11.5px;font-weight:${c.weight};color:${c.color};cursor:pointer;display:flex;align-items:center;justify-content:center;gap:3px`
                      )}
                    >
                      {c.label}
                      <i className={`icon-${c.icon}`} style={s(`font-size:11px`)}></i>
                    </button>
                  </React.Fragment>
                ))}
              </div>
              {rows?.map((r, r_idx) => (
                <React.Fragment key={r_idx}>
                  <div
                    onClick={r.onClick}
                    title={`${r.cmName} · ${r.alias}`}
                    style={s(
                      `display:grid;grid-template-columns:44px minmax(90px,1.1fr) 64px minmax(96px,1.1fr) 64px repeat(3,minmax(80px,1fr)) minmax(96px,1.1fr) 60px;gap:8px;align-items:center;padding:7px 6px;border-bottom:1px solid #F4EEE3;font-size:12.5px;color:#1B1B1B;text-align:center;cursor:pointer`
                    )}
                  >
                    <span style={s(`display:flex;justify-content:center`)}>
                      <span
                        style={s(
                          `width:22px;height:22px;border-radius:50%;background:${r.medalBg};border:1.5px solid ${r.medalBorder};color:${r.medalColor};font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center`
                        )}
                      >
                        {r.rank}
                      </span>
                    </span>
                    <span>{r.cmId}</span>
                    <span style={s(`color:${r.stageColor};font-weight:500`)}>{r.stage}</span>
                    <span>{r.capital}</span>
                    <span style={s(`color:${r.payoutColor};font-size:${r.payoutSize};font-weight:700`)}>{r.payoutMark}</span>
                    <span>{r.discipline}</span>
                    <span>{r.risk}</span>
                    <span>{r.consistency}</span>
                    <span style={s(`font-weight:700`)}>{r.score}</span>
                    <span
                      title={r.trendTitle}
                      style={s(`color:${r.trendColor};font-size:${r.trendSize};font-weight:700;letter-spacing:1px`)}
                    >
                      {r.trendMark}
                    </span>
                  </div>
                </React.Fragment>
              ))}
              {Boolean(noRows) && (
                <div style={s(`padding:32px 20px;text-align:center;color:#6B6358;font-size:13px`)}>
                  {t('managerNetwork.top.noMatch', 'No Capital Managers match the current search and filters.')}
                </div>
              )}
            </div>
          </div>
        )}

        {Boolean(hasMore) && (
          <div style={s(`display:flex;justify-content:center`)}>
            <button
              onClick={toggleMore}
              style={s(
                `height:${moreH};padding:0 20px;border:1px solid #E6DCCB;border-radius:6px;background:#fff;color:#3A342C;font-size:12.5px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:500;box-shadow:0 1px 2px rgba(0,0,0,0.03)`
              )}
            >
              {(moreLabel.includes('Đang tải') || moreLabel.includes('Loading')) && (
                <i className="icon-refresh" style={s(`animation:spin 1s linear infinite`)}></i>
              )}
              {moreLabel}
            </button>
          </div>
        )}
      </section>
      <div style={s(`font-size:11.5px;color:#9AA3AF;padding:4px 0 12px`)}>
        {t('managerNetwork.top.connectedApi', 'Connected to FTMO API: /capital-managers, /capital-managers/dashboard, /capital-managers/detail')}
      </div>
    </>
  );
};
